import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { loadSampleData } from '../lib/sampleData';
import { apiPost } from '../lib/api';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string, role?: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const WARNING_TIME = 2 * 60 * 1000; // 2 minutes before timeout

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Check for existing session on mount
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    
    if (storedUser && token) {
      setUser(JSON.parse(storedUser));
    }
    setIsLoading(false);
  }, []);

  // Session timeout and activity tracking
  useEffect(() => {
    if (!user) return;

    const updateActivity = () => setLastActivity(Date.now());
    
    // Track user activity
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => window.addEventListener(event, updateActivity));

    // Check session timeout
    const timeoutCheck = setInterval(() => {
      const inactive = Date.now() - lastActivity;
      
      if (inactive > SESSION_TIMEOUT) {
        logout();
        alert('Session expired due to inactivity. Please log in again.');
      } else if (inactive > SESSION_TIMEOUT - WARNING_TIME) {
        const remaining = Math.ceil((SESSION_TIMEOUT - inactive) / 1000);
        console.warn(`Session will expire in ${remaining} seconds`);
      }
    }, 30000); // Check every 30 seconds

    return () => {
      events.forEach(event => window.removeEventListener(event, updateActivity));
      clearInterval(timeoutCheck);
    };
  }, [user, lastActivity]);

  const login = async (email: string, password: string, role: string = 'RN') => {
    setIsLoading(true);
    try {
      const isDemoMode = import.meta.env.VITE_DEMO_MODE === 'true';
      console.log('🔍 Demo Mode Check:', {
        VITE_DEMO_MODE: import.meta.env.VITE_DEMO_MODE,
        isDemoMode,
        allEnvVars: import.meta.env
      });

      if (isDemoMode) {
        // Demo mode: Accept any credentials
        const authUser: User = {
          id: `demo-${role.toLowerCase()}`,
          email: email,
          name: email.split('@')[0] || 'Demo User',
          role: role.toUpperCase(),
        };

        localStorage.setItem('user', JSON.stringify(authUser));
        localStorage.setItem('token', 'demo-token-' + Date.now());
        setUser(authUser);
        setLastActivity(Date.now());

        // Load sample data into IndexedDB for demo/offline
        await loadSampleData();
      } else {
        // Production mode: Authenticate with API
        const response = await apiPost('/auth/login', { email, password, role });
        const { access_token, user: userPayload } = response;

        const authUser: User = {
          id: userPayload.id,
          email: userPayload.email,
          name: userPayload.name || userPayload.email,
          role: userPayload.role || role,
        };

        localStorage.setItem('user', JSON.stringify(authUser));
        localStorage.setItem('token', access_token);
        setUser(authUser);
        setLastActivity(Date.now());

        // Load sample data into IndexedDB for demo/offline
        await loadSampleData();
      }
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        isAuthenticated: !!user,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
